import type { PropertyDef, SelectOption, StatusOption } from "@bundle/contracts";
import { Db } from "@bundle/core";
import { useState } from "react";
import { Icon, Pressable, ScrollView, Stack, Text, TextField } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { statusGroupLabel } from "./labels.ts";
import { Tag } from "./Tag.tsx";

export type OptionPickerProps = {
  prop: PropertyDef;
  /** Selected option ids. */
  selected: string[];
  /** Several options (multi-select) or one. */
  multiple: boolean;
  onChange: (optionIds: string[]) => void;
  /** Creates an option inline (none when read-only). */
  onCreate?: (name: string) => SelectOption | undefined;
  onDone: () => void;
  testID: string;
};

/** Search, pick (or toggle) and create select / multi-select / status options. */
export function OptionPicker({ prop, selected, multiple, onChange, onCreate, onDone, testID }: OptionPickerProps) {
  const t = useT();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const status: StatusOption[] = prop.type === "status" ? Db.statusOptionsInOrder(prop.statusOptions ?? []) : [];
  const options: SelectOption[] = prop.type === "status" ? status : (prop.options ?? []);
  const visible = options.filter((o) => q === "" || o.name.toLowerCase().includes(q));
  const exact = options.find((o) => o.name.toLowerCase() === q);

  const pick = (id: string) => {
    if (multiple) {
      onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
      setQuery("");
      return;
    }
    onChange(selected.includes(id) ? [] : [id]);
    onDone();
  };
  const create = () => {
    if (onCreate === undefined || q === "") return;
    const o = onCreate(query);
    if (o !== undefined) pick(o.id);
  };

  const row = (o: SelectOption, group?: StatusOption["group"]) => (
    <Pressable key={o.id} direction="horizontal" radius="sm" onPress={() => pick(o.id)} selected={selected.includes(o.id)} testID={`${testID}-option-${o.id}`}>
      <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
        <Stack grow direction="horizontal" align="center">
          <Tag name={o.name} color={o.color} statusGroup={group} />
        </Stack>
        {selected.includes(o.id) ? <Icon name="check" size="icon.sm" color="accent.text" /> : null}
      </Stack>
    </Pressable>
  );

  return (
    <Stack gap="1" padding="2" testID={testID}>
      <TextField
        value={query}
        onChange={setQuery}
        placeholder={t("db.option.search")}
        autoFocus
        onSubmit={() => (exact !== undefined ? pick(exact.id) : create())}
        onCancel={onDone}
        testID={`${testID}-search`}
      />
      <ScrollView>
        <Stack gap="0.5" paddingY="1">
          {prop.type === "status"
            ? (["todo", "in_progress", "complete"] as const).map((g) => {
                const inGroup = visible.filter((o) => status.find((s) => s.id === o.id)?.group === g);
                return inGroup.length === 0 ? null : (
                  <Stack key={g} gap="0.5">
                    <Stack paddingX="2" paddingY="1">
                      <Text variant="caption" weight="medium" color="text.tertiary">
                        {t(statusGroupLabel(g))}
                      </Text>
                    </Stack>
                    {inGroup.map((o) => row(o, g))}
                  </Stack>
                );
              })
            : visible.map((o) => row(o))}
          {onCreate !== undefined && q !== "" && exact === undefined ? (
            <Pressable direction="horizontal" radius="sm" onPress={create} testID={`${testID}-create`}>
              <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
                <Icon name="plus" size="icon.sm" color="text.secondary" />
                <Text variant="footnote" lines={1}>
                  {t("db.option.create", { name: query.trim() })}
                </Text>
              </Stack>
            </Pressable>
          ) : null}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
